import { Button, cn, Dialog, DialogClose, DialogContent, DialogPortal, Icon, Text } from '@flatfile/baseline'
import { useEntitlements } from '../../hooks/useEntitlements'
import { useMemo, useState } from 'react'
import { useIdentity } from '../../hooks/useIdentity'
import { FlatfileLogoSquare } from '../../assets/logo-square'

type TrialBannerProps = {
  className?: string
}

const normalizeCalendarLink = (link: string) => {
  if (!link.startsWith('http://') && !link.startsWith('https://')) {
    return `https://${link}`
  }
  return link
}

const TrialBanner = ({ className }: TrialBannerProps) => {
  const { hasEntitlement, getEntitlementMetadata } = useEntitlements()
  const [modalOpen, setModalOpen] = useState(false)
  const { identity } = useIdentity()
  const firstName = identity?.user?.name?.split(' ')[0] || 'there'
  const metadata = getEntitlementMetadata('trialEnabled')

  const daysLeft = useMemo(() => {
    if (!hasEntitlement('trialEnabled')) return 0

    const expiresAt = (metadata as any)?.expiresAt as string | undefined

    if (!expiresAt) return 0

    const now = new Date()
    const trialEndDate = new Date(expiresAt)

    return Math.max(0, Math.ceil((trialEndDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24)))
  }, [hasEntitlement, metadata])

  if (!hasEntitlement('trialEnabled')) {
    return null
  }

  return (
    <div className={cn('flex items-center', className)}>
      <button className='flex gap-1 items-center' onClick={() => setModalOpen(true)}>
        <Text>{daysLeft} days left in trial</Text>
        <Icon name='circle-info' variant='outline' size={16} />
      </button>

      <Dialog open={modalOpen} onOpenChange={setModalOpen}>
        <DialogPortal>
          <DialogContent className='flex flex-col items-center text-center w-80'>
            <DialogClose asChild>
              <Icon
                name='cross-large'
                variant='outline'
                className='absolute top-3 right-3 cursor-pointer text-muted hover:text-base-black'
              />
            </DialogClose>
            <div className='pt-4'>
              <FlatfileLogoSquare size={32} fill='#000000' />
            </div>
            <Text emphasized>
              Hi, {firstName}.
              <br />
              Trial ending in {daysLeft} days.
            </Text>
            <Text className='text-muted'>
              Looks like your trial is wrapping up. Let's chat about what's next — reach out and we'll get you set up.
            </Text>
            <a
              className='w-full'
              href={normalizeCalendarLink((metadata as any)?.calendarLink)}
              target='_blank'
              rel='noreferrer'
            >
              <Button className='w-full'>Talk to us</Button>
            </a>
          </DialogContent>
        </DialogPortal>
      </Dialog>
    </div>
  )
}

export default TrialBanner
